/**
 * FECHAS EN HORA DE EL SALVADOR.
 *
 * El sistema vive en El Salvador (UTC-6) y el servidor va en UTC. Todo
 * `new Date().toISOString().split("T")[0]` que había repartido por el frontend
 * devolvía la fecha **en UTC**, así que **a partir de las 6 de la tarde de El
 * Salvador ya decía el día siguiente**. Consecuencias que se vieron en
 * producción:
 *
 * · El panel del guardia abría mostrando **mañana** y las visitas de su turno
 *   desaparecían — justo en las horas de más movimiento en una caseta.
 * · El formulario del residente ponía como fecha mínima «pasado mañana», así
 *   que de noche **no se podía crear una visita para el día siguiente**.
 * · El contador de «visitas de hoy» contaba el día equivocado.
 *
 * Es el mismo fallo que ya se había corregido en el backend (la validez del QR
 * «sólo el día de la visita»), pero el frontend se quedó fuera de aquella
 * corrección.
 *
 * `en-CA` se usa porque su formato de fecha corta es exactamente `YYYY-MM-DD`,
 * que es lo que esperan los `<input type="date">` y el API.
 */

const ZONA = 'America/El_Salvador';

/** Hoy en El Salvador, como `YYYY-MM-DD`. */
export const hoySV = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: ZONA });

/**
 * Un día relativo a hoy en El Salvador, como `YYYY-MM-DD`.
 * `enDiasSV(1)` es mañana; `enDiasSV(-1)`, ayer.
 */
export const enDiasSV = (dias) =>
  new Date(Date.now() + dias * 86400000).toLocaleDateString('en-CA', { timeZone: ZONA });

/**
 * Una fecha concreta a `YYYY-MM-DD`, **sin cambiar de día**.
 *
 * Para fechas que ya se construyeron en la zona del navegador —el mes que el
 * calendario está mostrando, por ejemplo—. Aquí NO se convierte de zona: se
 * leen los componentes locales, porque convertir correría el día.
 */
export const aISO = (fecha) => {
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return '';
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};

/**
 * Un DÍA DEL CALENDARIO que viene del servidor, escrito para leerlo.
 *
 * Ojo, porque esto se corrió una vez y es el mismo día de diferencia que todo
 * lo de arriba, pero al revés. Los campos de día —la fecha de un evento, el
 * cierre de una encuesta— se escogen en un `<input type="date">`, que manda
 * `"2026-11-05"` a secas. Mongoose lo convierte a `Date` **y una fecha sin
 * hora se interpreta en UTC**, así que en la base queda la medianoche UTC y
 * el API devuelve `"2026-11-05T00:00:00.000Z"`.
 *
 * Formatear eso en hora de El Salvador (UTC-6) da las 6 de la tarde del día
 * ANTERIOR: un evento del jueves 5 se mostraba como «miércoles, 4 de
 * noviembre». Por eso aquí se lee en **UTC**: no es un descuido, es que ese
 * valor nunca tuvo hora, y leerlo en la zona en que se escribió devuelve
 * exactamente el día que la persona escogió.
 *
 * Sólo para valores que llegan del servidor. Un `Date` armado con componentes
 * locales en el navegador no sirve aquí —ahí el día correcto es el local, y
 * eso lo hace `aISO`—. Y para una marca de tiempo con hora de verdad (cuándo
 * entró una visita) hace falta lo contrario: convertir a la zona de El
 * Salvador, que es la hora que la persona vivió.
 */
export const mostrarDia = (fecha, opciones = { dateStyle: 'full' }) => {
  if (!fecha) return '';
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-SV', { timeZone: 'UTC', ...opciones });
};

/**
 * El DÍA DEL CALENDARIO de un valor del servidor, como `YYYY-MM-DD`.
 *
 * La pareja de `mostrarDia` para **comparar** en vez de mostrar, y existe por
 * la misma razón: el valor llegó como medianoche UTC, así que el día hay que
 * leerlo en UTC o sale el anterior. Lo que antes se hacía con
 * `toDateString()` o con `esHoySV` leía la zona del navegador, y en UTC-6 eso
 * metía las visitas en la casilla de ayer del calendario y hacía que el
 * contador de «hoy» contara el día equivocado.
 *
 * Compara contra `hoySV()` —hoy— o contra `aISO(fechaLocal)` —el día que
 * dibuja el calendario—: las tres hablan el mismo formato a propósito.
 */
export const diaISO = (fecha) => {
  if (!fecha) return '';
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', { timeZone: 'UTC' });
};

/** ¿Esta fecha cae hoy, en El Salvador? */
export const esHoySV = (fecha) => {
  if (!fecha) return false;
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return false;
  return d.toLocaleDateString('en-CA', { timeZone: ZONA }) === hoySV();
};
